import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Menu, Globe } from 'lucide-react';

export default function Header() {
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/about', label: t('nav.about') },
    { to: '/services', label: t('nav.services') },
    { to: '/articles', label: t('nav.articles') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header data-testid="main-header" className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <span className="text-[#C5A059]">ÖZTÜRK</span>
              <span className="text-[#F3F4F6] font-light ml-1">HUKUK</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map(link => (
              <Link key={link.to} to={link.to} data-testid={`nav-${link.to.replace('/', '') || 'home'}`}
                className={`px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-200 rounded-sm ${isActive(link.to) ? 'text-[#C5A059]' : 'text-[#A1A1AA] hover:text-[#F3F4F6]'}`}>
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-[#A1A1AA] hover:text-[#C5A059] hover:bg-transparent" data-testid="language-toggle">
                  <Globe className="h-4 w-4 mr-1" />
                  <span className="text-xs uppercase tracking-wider">{language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#121212] border-white/10">
                <DropdownMenuItem onClick={() => setLanguage('tr')} className="text-[#F3F4F6] hover:text-[#C5A059] cursor-pointer">Türkçe</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('en')} className="text-[#F3F4F6] hover:text-[#C5A059] cursor-pointer">English</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* İletişim Button (Desktop) */}
            <Link to="/contact" className="hidden lg:block">
              <Button size="sm" className="bg-[#C5A059] text-[#0A0A0A] hover:bg-[#D4AF6A] font-medium tracking-wide rounded-sm" data-testid="nav-contact-btn">
                {t('nav.contact')}
              </Button>
            </Link>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="sm" className="text-[#A1A1AA]" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#0A0A0A] border-white/10 w-72">
                <nav className="flex flex-col gap-2 mt-8">
                  {navLinks.map(link => (
                    <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}
                      className={`px-4 py-3 text-base transition-colors rounded-sm ${isActive(link.to) ? 'text-[#C5A059] bg-white/5' : 'text-[#A1A1AA] hover:text-[#F3F4F6]'}`}>
                      {link.label}
                    </Link>
                  ))}
                  <Link to="/contact" onClick={() => setMobileOpen(false)} className="px-4 py-3 text-[#C5A059] font-medium">{t('nav.contact')}</Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
